import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { User, Phone, CreditCard, Briefcase, ArrowRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { OnboardingProgress } from "@/components/Onboarding/OnboardingProgress";
import AuthLeftPanel from "@/components/Auth/AuthLeftPanel";

// Validadores brasileiros
function isValidCPF(cpf: string): boolean {
  const cleanCPF = cpf.replace(/\D/g, "");
  if (cleanCPF.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleanCPF)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  return remainder === parseInt(cleanCPF.charAt(10));
}

function formatCPF(value: string): string {
  const cleaned = value.replace(/\D/g, "").slice(0, 11);
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
  if (cleaned.length <= 9) return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
}

function formatPhone(value: string): string {
  const cleaned = value.replace(/\D/g, "").slice(0, 11);
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 7) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
}

const personalSchema = z.object({
  nome: z.string().min(3, "Nome deve ter pelo menos 3 caracteres"),
  telefone: z
    .string()
    .min(14, "Telefone inválido")
    .refine((val) => val.replace(/\D/g, "").length >= 10, "Telefone inválido"),
  cpf: z
    .string()
    .min(14, "CPF inválido")
    .refine((val) => isValidCPF(val), "CPF inválido"),
  cargo: z.string().min(2, "Cargo deve ter pelo menos 2 caracteres"),
});

type PersonalFormData = z.infer<typeof personalSchema>;

export default function PersonalData() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [cpfError, setCpfError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PersonalFormData>({
    resolver: zodResolver(personalSchema),
  });

  // Verificar autenticação e email confirmado
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      if (!session.user.email_confirmed_at) {
        navigate("/verify-email");
        return;
      }

      // Verificar etapa do onboarding
      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarding_step, full_name")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        // Se já completou esta etapa, ir para a próxima
        if (profile.onboarding_step === "company" || 
            profile.onboarding_step === "plan" || 
            profile.onboarding_step === "completed") {
          navigate("/onboarding/company");
          return;
        }
        
        // Preencher nome se já existir
        if (profile.full_name) {
          setValue("nome", profile.full_name);
        }
      }

      setIsCheckingAuth(false);
    };

    checkAuth();
  }, [navigate, setValue]);

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("cpf", formatCPF(e.target.value));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("telefone", formatPhone(e.target.value));
  };

  const onSubmit = async (data: PersonalFormData) => {
    setIsLoading(true);
    setCpfError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }
      const cleanCPF = data.cpf.replace(/\D/g, "");

      // Verificar se CPF já existe (excluindo o usuário atual)
      const { data: existingUser } = await supabase
        .from("users")
        .select("id")
        .eq("cpf", cleanCPF)
        .neq("id", session.user.id)
        .maybeSingle();

      if (existingUser) {
        setCpfError("Este CPF já está cadastrado no sistema.");
        toast({
          title: "CPF já cadastrado",
          description: "Este CPF já está vinculado a outra conta.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Atualizar profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          full_name: data.nome,
          onboarding_step: "personal",
        })
        .eq("id", session.user.id);

      if (profileError) throw profileError;

      // Atualizar users
      const { error: userError } = await supabase
        .from("users")
        .update({
          name: data.nome,
          phone: data.telefone.replace(/\D/g, ""),
          cpf: cleanCPF,
          cargo: data.cargo,
        })
        .eq("id", session.user.id);

      if (userError) throw userError;

      // Log de auditoria
      await supabase.from("audit_logs").insert({
        user_id: session.user.id,
        event_type: "onboarding_personal_completed",
        metadata: { step: "personal" },
      });

      toast({
        title: "Dados salvos",
        description: "Suas informações pessoais foram salvas.",
      });

      navigate("/onboarding/company");

    } catch (error: any) {
      console.error("[PersonalData] Erro:", error);
      toast({
        title: "Erro ao salvar",
        description: error.message || "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row">
      <AuthLeftPanel />
      <div 
        className="flex-1 flex items-center justify-center bg-gradient-to-tl from-amber-50 via-orange-50 to-white min-h-screen lg:min-h-0 p-4 sm:p-6 lg:p-8 overflow-y-auto"
        style={{
          backgroundImage: 'url(/logos/images/fundo_apresentacao_eleeva.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <Card className="w-full max-w-md border-2 border-amber-200 bg-white/95 backdrop-blur-sm shadow-2xl shadow-amber-200/20">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Dados Pessoais</CardTitle>
            <CardDescription>
              Complete suas informações para continuar
            </CardDescription>
            <div className="mt-4">
              <OnboardingProgress currentStep={1} totalSteps={4} />
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="nome">Nome completo</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="nome"
                    placeholder="Seu nome completo"
                    className="pl-10"
                    {...register("nome")}
                  />
                </div>
                {errors.nome && (
                  <p className="text-sm text-destructive">{errors.nome.message}</p>
                )}
              </div>

              {/* Telefone */}
              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="telefone"
                    placeholder="(00) 00000-0000"
                    className="pl-10"
                    {...register("telefone")}
                    onChange={handlePhoneChange}
                  />
                </div>
                {errors.telefone && (
                  <p className="text-sm text-destructive">{errors.telefone.message}</p>
                )}
              </div>

              {/* CPF */}
              <div className="space-y-2">
                <Label htmlFor="cpf">CPF</Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="cpf"
                    placeholder="000.000.000-00"
                    className="pl-10"
                    {...register("cpf")}
                    onChange={handleCPFChange}
                  />
                </div>
                {(errors.cpf || cpfError) && (
                  <p className="text-sm text-destructive">{errors.cpf?.message || cpfError}</p>
                )}
              </div>

              {/* Cargo */}
              <div className="space-y-2">
                <Label htmlFor="cargo">Cargo</Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="cargo"
                    placeholder="Ex: Gerente de TI"
                    className="pl-10"
                    {...register("cargo")}
                  />
                </div>
                {errors.cargo && (
                  <p className="text-sm text-destructive">{errors.cargo.message}</p>
                )}
              </div>

              {/* Botão */}
              <Button type="submit" className="w-full gap-2" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    Continuar
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
